<template>
  <div class="download-center-container">
    <div class="search">
      <el-form :inline="true" :model="searchModel">
        <el-form-item>
          <el-select v-model="searchModel.timeType" width="140px">
            <el-option label="申请导出时间" value="exportTime" />
            <el-option label="最近下载时间" value="downloadTime" />
          </el-select>
        </el-form-item>

        <el-form-item>
          <date-picker-erp
            :is-range="true"
            date-width="170px"
            value-format="timestamp"
            v-model="searchModel.customTime"
            :default-time="['00:00:00', '23:59:59']"
          />
        </el-form-item>

        <el-form-item>
          <el-button type="primary" @click="getList">查询</el-button>
          <el-button @click="resetSearch" class="ml-10">清空条件</el-button>
          <span class="red ml-10">导出过程需要1-2分钟，如遇未导出成功，请刷新页面重试！</span>
        </el-form-item>
      </el-form>
    </div>

    <div class="toolbar">
      <el-button type="link" @click="handleBatchDownload">
        <span></span>
        <span>批量下载</span>
      </el-button>
      <el-button type="link" @click="handleBatchDelete">批量删除</el-button>
    </div>

    <div class="main">
      <el-table :data="list" style="width: 100%" :height="scrollerHeight" @selection-change="handleSelectionChange">
        <el-table-column type="index" width="50"> </el-table-column>
        <el-table-column type="selection" width="55"> </el-table-column>
        <el-table-column prop="exportTime" label="申请导出时间" width="140">
          <template slot-scope="{ row }">{{ row.exportTime | formatTime }}</template>
        </el-table-column>
        <el-table-column prop="moduleName" label="模块" width="100"> </el-table-column>
        <el-table-column prop="content" label="导出内容" width="320">
          <template slot-scope="{ row }">
            <p class="ellipsis1">{{ row.content }}</p>
          </template>
        </el-table-column>
        <el-table-column prop="name" label="进度" width="120">
          <template slot="header" slot-scope="scope">
            <ScheduleHeader :rows="scope" :searchModel="searchModel" @on-schedules="handleScheduleChange" />
          </template>
          <template slot-scope="{ row }">{{ row.schedule | formatSchedule }}</template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" width="130"> </el-table-column>
        <el-table-column prop="downloadTime" label="最近下载时间" width="140">
          <template slot-scope="{ row }">{{ row.downloadTime | formatTime }}</template>
        </el-table-column>
        <el-table-column prop="downloadCount" label="下载次数" width="100"> </el-table-column>
        <el-table-column prop="name" label="操作">
          <template slot-scope="{ row }">
            <el-button :disabled="!hasDownload(row)" type="link" @click="handleDownload(row)">下载</el-button> |
            <el-button :disabled="!hasDelete(row)" type="link" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="mt-10"
        @current-change="getList"
        @size-change="handleSizeChange"
        :current-page="paginationModel.pageNo"
        :page-sizes="[20, 30, 40, 50, 100, 300]"
        :page-size="paginationModel.pageSize"
        :pager-count="5"
        layout="total, sizes, prev, pager, next, jumper"
        :total="paginationModel.total"
        style="text-align: right"
      >
      </el-pagination>
    </div>

    <verifyDialog ref="verifyDialog" @on-success="handleVerifyDownload" />
  </div>
</template>

<script>
import dayjs from 'dayjs';
import verifyDialog from './verifyDialog.vue';
import ScheduleHeader from './scheduleHeader.vue';
import { addWindowResizeHandler } from './service.js';
import {
  deleteDownloadCenter,
  getDownloadCenterList,
  isVerifyDownloadCenter,
  deleteBatchDownloadCenter,
  downloadFileDownloadCenter,
  downloadFileByVerifyDownloadCenter,
} from '@api/trade/downloadCenter.js';
import { getErpHost } from '@/utils/domainHelper.js';

export default {
  name: 'DownloadCenter',
  components: { ScheduleHeader, verifyDialog },
  data() {
    return {
      // 表格
      list: [],
      // 表格选中数据
      selectedList: [],

      scrollerHeight: 0,

      paginationModel: {
        pageNo: 1,
        pageSize: 20,
        total: 0,
      },
      searchModel: {
        schedules: [],
        timeType: 'exportTime',
        customTime: {
          start: dayjs().subtract(7, 'day').startOf('day').unix() * 1000,
          end: '',
        },
      },

      id: '',

      currentState: '',
    };
  },
  mounted() {
    this.init();
    this.getList();
  },
  methods: {
    // 初始化
    init() {
      // 获取屏幕实时高度
      this.$nextTick(() => {
        this.scrollerHeight = window.innerHeight - 180 + 'px';
      });
      addWindowResizeHandler(() => {
        this.$nextTick(() => {
          this.scrollerHeight = window.innerHeight - 180 + 'px';
        });
      });
    },
    // 清空条件
    resetSearch() {
      this.paginationModel = {
        pageNo: 1,
        pageSize: 20,
      };
      this.searchModel = {
        schedules: [],
        timeType: 'exportTime',
        customTime: {
          start: dayjs().subtract(7, 'day').startOf('day').unix() * 1000,
          end: '',
        },
      };
      this.getList();
    },
    // 获取请求参数
    getParams() {
      const { pageNo, pageSize } = this.paginationModel;
      const { timeType, customTime, schedules = [] } = this.searchModel;
      return {
        // 快麦通业务暂时无关联字段
        module: '',
        // 默认参数
        pageNo,
        pageSize,
        timeType,
        schedules: schedules.join(','),
        startDate: customTime.start || '',
        endDate: customTime.end || '',
      };
    },
    // 获取列表
    async getList() {
      const { list = [], total = 0 } = await getDownloadCenterList(this.getParams());
      this.list = list;
      this.paginationModel.total = total;
    },

    // 是否可下载
    hasDownload({ schedule }) {
      return [3].includes(schedule);
    },
    // 下载
    async handleDownload({ id }) {
      this.id = id;
      this.currentState = 'download';
      // 身份校验
      const data = await this.checkVerify(id);
      if (data.isVerify) {
        this.$refs?.verifyDialog?.show(data);
        return;
      }
      this.singleDownload({ id });
    },
    // 短信校验单个下载
    async handleVerifyDownload(params) {
      // 如果是 批量操作
      if (this.currentState === 'batchDownload') {
        this.batchDownload(params);
        return;
      }

      const res = await downloadFileByVerifyDownloadCenter({
        id: this.id,
        ...params,
      });
      if (res.result === 1) {
        this.$refs?.verifyDialog?.success();
        this.singleDownload({ id: this.id });
      }
    },
    // 下载文件
    async singleDownload(params) {
      const { fileUrls = [] } = await downloadFileDownloadCenter(params);
      // 刷新列表
      this.getList();
      // 下载
      let url = fileUrls[0].fileUrl;
      let urlArr = url.split('://');
      let urlSecond = urlArr[1].split('/');
      urlSecond[urlSecond.length - 1] = encodeURIComponent(urlSecond[urlSecond.length - 1]);
      location.href = 'https://' + urlSecond.join('/');
    },
    // 批量下载
    async handleBatchDownload() {
      this.currentState = 'batchDownload';
      if (!this.selectedList.length) {
        return;
      }
      const ids = this.selectedList.map(item => item.id).join(',');
      // 身份校验
      const data = await this.checkVerify(ids);
      if (data.isVerify) {
        this.$refs?.verifyDialog?.show(data);
        return;
      }
      this.batchDownload();
    },
    batchDownload(params = {}) {
      const { phoneNumber, verifyCode } = params;
      const ids = this.selectedList.map(item => item.id).join(',');

      if (phoneNumber !== undefined && verifyCode !== undefined) {
        window.open(`${getErpHost()}downloadCenter/downloadFilesByVerify?ids=${ids}&phoneNumber=${phoneNumber}&verifyCode=${verifyCode}`);
        return;
      }
      window.open(`${getErpHost()}downloadCenter/downloadFiles?ids=${ids}`);
    },
    // 检测是否需要身份验证
    async checkVerify(ids) {
      const data = await isVerifyDownloadCenter({ ids });
      return data;
    },

    // 是否可删除
    hasDelete({ schedule }) {
      return [3, 4].includes(schedule);
    },
    // 删除
    async handleDelete(row) {
      await deleteDownloadCenter({ id: row.id });
      this.getList();
    },
    // 批量删除
    async handleBatchDelete() {
      if (!this.selectedList.length) {
        return;
      }
      const ids = this.selectedList.map(item => item.id).join(',');
      await deleteBatchDownloadCenter({ ids });
      this.$message.success('批量操作成功');
      this.getList();
    },

    // 选中表格数据
    handleSelectionChange(selectedList) {
      this.selectedList = selectedList;
    },
    // 处理 进度
    handleScheduleChange(val) {
      this.searchModel.schedules = val.schedules;
      this.getList();
    },
    // 分页
    handleSizeChange(val) {
      this.paginationModel.pageNo = 1;
      this.paginationModel.pageSize = val;
      this.getList();
    },
  },
  filters: {
    formatTime(val) {
      return val ? dayjs(val).format('YYYY-MM-DD HH:mm:ss') : '--';
    },
    formatSchedule(val) {
      if (!val) {
        return '-';
      }
      var map = {
        1: '排队中',
        2: '导出中',
        3: '导出完成',
        4: '失败',
      };
      return map[val];
    },
  },
};
</script>

<style lang="scss" scoped>
.download-center-container {
  padding: 20px;
  ::v-deep .el-form-item {
    margin-bottom: 10px !important;
  }
  .el-button {
    margin-left: 0 !important;
  }
  .red {
    color: red;
    font-weight: 800px;
  }
  .ml-10 {
    margin-left: 10px !important;
  }
  .ellipsis1 {
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
  }
}
</style>
